import posthog from 'posthog-js'
import * as Sentry from '@sentry/react'
import { resolveAppTelemetryConfig } from './telemetryConfig'
import {
  isTelemetryOptedOut,
  setTelemetryOptOutPreference,
} from './telemetryPreferences'

export const PATTER_TELEMETRY_NOTICE =
  'Playtest telemetry sends gameplay summaries and error reports to help tune balance and fix bugs. It does not send exact words played.'

type TelemetryPropertyValue =
  | boolean
  | null
  | number
  | string
  | readonly unknown[]
  | undefined
  | TelemetryProperties

interface TelemetryProperties {
  [key: string]: TelemetryPropertyValue
}

let posthogInitialized = false
let sentryInitialized = false

function canUseBrowserTelemetry() {
  return typeof window !== 'undefined'
}

function stripUndefinedProperties(properties: TelemetryProperties) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined),
  ) as Record<string, unknown>
}

export function initializeAppTelemetry() {
  if (!canUseBrowserTelemetry() || isTelemetryOptedOut()) {
    return
  }

  const config = resolveAppTelemetryConfig()

  if (config.posthog && !posthogInitialized) {
    posthog.init(config.posthog.key, {
      api_host: config.posthog.host,
      autocapture: false,
      capture_pageleave: false,
      capture_pageview: false,
      disable_session_recording: true,
      person_profiles: 'identified_only',
    })
    posthogInitialized = true
  }

  if (config.sentry && !sentryInitialized) {
    Sentry.init({
      dsn: config.sentry.dsn,
      environment: config.environment,
      tracesSampleRate: 0,
      enableLogs: true,
      beforeSend: event => (isTelemetryOptedOut() ? null : event),
      dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
          request: false,
          response: false,
        },
        httpBodies: [],
        queryParams: false,
        genAI: {
          inputs: false,
          outputs: false,
        },
        stackFrameVariables: false,
      },
    })
    sentryInitialized = true
  }
}

export function captureProductTelemetry(eventName: string, properties: TelemetryProperties) {
  if (!canUseBrowserTelemetry() || isTelemetryOptedOut()) {
    return
  }

  initializeAppTelemetry()

  if (!posthogInitialized) {
    return
  }

  const config = resolveAppTelemetryConfig()

  posthog.capture(
    eventName,
    stripUndefinedProperties({
      ...properties,
      appEnvironment: config.environment,
    }),
  )
}

export function addTelemetryBreadcrumb({
  category,
  data,
  level = 'info',
  message,
}: {
  category: string
  data?: TelemetryProperties
  level?: Sentry.SeverityLevel
  message: string
}) {
  if (!sentryInitialized || isTelemetryOptedOut()) {
    return
  }

  Sentry.addBreadcrumb({
    category,
    data: data ? stripUndefinedProperties(data) : undefined,
    level,
    message,
  })
}

export function setTelemetryTags(tags: TelemetryProperties) {
  if (!sentryInitialized || isTelemetryOptedOut()) {
    return
  }

  for (const [key, value] of Object.entries(stripUndefinedProperties(tags))) {
    if (value === null) {
      continue
    }
    Sentry.setTag(key, String(value))
  }
}

export function captureTelemetryException(error: unknown, context?: TelemetryProperties) {
  if (!sentryInitialized || isTelemetryOptedOut()) {
    return
  }

  Sentry.withScope(scope => {
    if (context) {
      scope.setContext('patter', stripUndefinedProperties(context))
    }
    Sentry.captureException(error)
  })
}

export function optOutTelemetry() {
  setTelemetryOptOutPreference(true)
  if (posthogInitialized) {
    posthog.opt_out_capturing()
  }
  if (sentryInitialized) {
    void Sentry.close(2000)
    sentryInitialized = false
  }
}

export function optInTelemetry() {
  setTelemetryOptOutPreference(false)
  if (posthogInitialized) {
    posthog.opt_in_capturing()
  }
  initializeAppTelemetry()
}

export function getTelemetryStatus() {
  const config = resolveAppTelemetryConfig()

  return {
    environment: config.environment,
    notice: PATTER_TELEMETRY_NOTICE,
    optedOut: isTelemetryOptedOut(),
    posthogEnabled: posthogInitialized && !isTelemetryOptedOut(),
    sentryEnabled: sentryInitialized && !isTelemetryOptedOut(),
  }
}

export function installTelemetryConsoleHelper() {
  if (!canUseBrowserTelemetry()) {
    return
  }

  window.patterTelemetry = {
    notice: PATTER_TELEMETRY_NOTICE,
    optIn: () => {
      optInTelemetry()
      return getTelemetryStatus()
    },
    optOut: () => {
      optOutTelemetry()
      return getTelemetryStatus()
    },
    status: getTelemetryStatus,
  }
}

declare global {
  interface Window {
    patterTelemetry?: {
      notice: string
      optIn: () => ReturnType<typeof getTelemetryStatus>
      optOut: () => ReturnType<typeof getTelemetryStatus>
      status: () => ReturnType<typeof getTelemetryStatus>
    }
  }
}
